import React, { Component } from 'react'
import { Col, Row } from 'reactstrap'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
} from '@mui/material'
import moment from 'moment'
import { SALE_TYPES, EMPTY_RULE, emptyCommission, ruleFor } from '../utils/affiliateCommission'
import { normaliseCode } from '../utils/affiliateSales'

export const CAMPAIGN_STATES = [
  { label: 'Luonnos', value: 'draft' },
  { label: 'Aktiivinen', value: 'active' },
  { label: 'Tauolla', value: 'paused' },
  { label: 'Päättynyt', value: 'ended' },
]

const STEPS = ['Perustiedot', 'Koodit ja voimassaolo', 'Palkkiot']

const DEFAULT_ATTRIBUTION_MINUTES = 60

export const EMPTY_CAMPAIGN = {
  name: '',
  affiliateid: '',
  affiliatename: '',
  codes: [],
  state: 'draft',
  start: '',
  end: '',
  attributionminutes: DEFAULT_ATTRIBUTION_MINUTES,
  landingurl: '',
  budgetcap: 0,
  commission: {},
  notes: '',
}

// The money fields are held as raw strings while being typed and parsed only on save, the
// same way ActivityCreator handles a price - so a half-typed '1,' does not become NaN.
const MONEY_FIELDS = ['value', 'minorder', 'maxpersale']

const toInput = (value) => {
  const number = Number(value)
  return isFinite(number) ? String(number).replace('.', ',') : '0'
}

const fromInput = (raw) => {
  if (raw === undefined || raw === null || raw === '') return 0
  const parsed = parseFloat(String(raw).replace(',', '.').replace(/[^0-9.]/g, ''))
  return isFinite(parsed) ? parsed : 0
}

/**
 * Create/edit form for an affiliate campaign: which codes earn, when they earn, and what
 * they earn per sale type.
 *
 * Create walks a Stepper; edit shows every section at once, the same split
 * ActivityCreator/ActivityDetailsWizard uses. Commission rules are the substance here -
 * `basis` and `per` exist because "10 %" is ambiguous between VAT-inclusive and
 * VAT-exclusive, and "2 EUR" is ambiguous between per order and per ticket, and a
 * commission invoice cannot be reconciled against an ambiguous rule.
 */
class AffiliateCampaignEditor extends Component {
  constructor(props) {
    super(props)
    const source = props.campaign || {}
    const campaign = {
      ...EMPTY_CAMPAIGN,
      ...source,
      // Arrays and nested objects need fresh copies - the spread above is shallow, and the
      // editor mutates its own state object in place.
      codes: (source.codes || []).map((entry) => ({ ...entry })),
      commission: { ...emptyCommission() },
    }
    SALE_TYPES.forEach((type) => {
      campaign.commission[type.key] = ruleFor(source, type.key)
    })
    if (!campaign.id && campaign.codes.length === 0) {
      campaign.codes = [{ code: '', label: '', state: 'active' }]
    }

    this.state = {
      campaign,
      activeStep: 0,
      moneyInputs: this.buildMoneyInputs(campaign),
      // Rules as they were loaded, so save can tell whether any of them actually moved.
      originalCommission: JSON.stringify(campaign.commission),
      saving: false,
    }
  }

  buildMoneyInputs = (campaign) => {
    const inputs = { budgetcap: toInput(campaign.budgetcap) }
    SALE_TYPES.forEach((type) => {
      MONEY_FIELDS.forEach((field) => {
        inputs[type.key + '.' + field] = toInput(campaign.commission[type.key][field])
      })
    })
    return inputs
  }

  /* Handlers */

  // Supports arbitrary depth, unlike the single-level parent.child handlers in
  // ActivityCreator - commission rules live two levels down.
  setPath = (property, value) => {
    const { campaign } = this.state
    const parts = property.split('.')
    let target = campaign
    for (let i = 0; i < parts.length - 1; i += 1) {
      target = target[parts[i]]
    }
    target[parts[parts.length - 1]] = value
    this.setState({ campaign })
  }

  handleTextChange = (e, property) => {
    this.setPath(property, e.target.value)
  }

  handleNumberChange = (e, property) => {
    const raw = e.target.value
    if (typeof raw === 'number') {
      this.setPath(property, raw)
    } else {
      this.setPath(property, fromInput(raw))
    }
  }

  handleSwitchChange = (e, property) => {
    this.setPath(property, e.target.checked)
  }

  handleMoneyChange = (e, key) => {
    const { moneyInputs } = this.state
    moneyInputs[key] = e.target.value
    this.setState({ moneyInputs })
  }

  /* Codes */

  handleCodeChange = (index, field, value) => {
    const { campaign } = this.state
    campaign.codes[index][field] = field === 'code' ? normaliseCode(value) : value
    this.setState({ campaign })
  }

  addCode = () => {
    const { campaign } = this.state
    campaign.codes.push({ code: '', label: '', state: 'active' })
    this.setState({ campaign })
  }

  removeCode = (index) => {
    const { campaign } = this.state
    campaign.codes.splice(index, 1)
    this.setState({ campaign })
  }

  /* Validation */

  // Codes are unique across every campaign of the venue, case-insensitively: a link is a
  // link, and two campaigns claiming the same code could not be told apart in reporting.
  codeError = (index) => {
    const { campaign } = this.state
    const code = normaliseCode(campaign.codes[index].code)
    if (!code) return 'Koodi puuttuu'
    const duplicateHere = campaign.codes.some((entry, i) => i !== index && normaliseCode(entry.code) === code)
    if (duplicateHere) return 'Koodi on jo tässä kampanjassa'
    const others = (this.props.campaigns || []).filter((c) => c.id !== campaign.id)
    const clash = others.find((c) => (c.codes || []).some((entry) => normaliseCode(entry.code) === code))
    if (clash) return 'Koodi on käytössä kampanjassa ' + (clash.name || clash.id)
    return ''
  }

  hasCodeErrors = () => this.state.campaign.codes.some((entry, index) => !!this.codeError(index))

  dateError = () => {
    const { start, end } = this.state.campaign
    if (start && end && moment(end).isBefore(moment(start))) return 'Päättymispäivä on ennen alkua'
    return ''
  }

  isValid = () => {
    const { campaign } = this.state
    if (!campaign.name || campaign.name.trim().length === 0) return false
    if (!campaign.affiliateid) return false
    if (campaign.codes.length === 0 || this.hasCodeErrors()) return false
    if (this.dateError()) return false
    return true
  }

  /* Save */

  // Money strings become numbers here and nowhere else.
  buildPayload = () => {
    const { campaign, moneyInputs } = this.state
    const payload = { ...campaign, commission: {} }
    payload.budgetcap = fromInput(moneyInputs.budgetcap)
    payload.attributionminutes = Number(campaign.attributionminutes) || DEFAULT_ATTRIBUTION_MINUTES
    SALE_TYPES.forEach((type) => {
      const rule = { ...EMPTY_RULE, ...campaign.commission[type.key] }
      MONEY_FIELDS.forEach((field) => {
        rule[field] = fromInput(moneyInputs[type.key + '.' + field])
      })
      payload.commission[type.key] = rule
    })
    payload.codes = campaign.codes.map((entry) => ({
      code: normaliseCode(entry.code),
      label: entry.label || '',
      state: entry.state || 'active',
    }))
    return payload
  }

  handleSave = async () => {
    if (!this.isValid()) return
    const payload = this.buildPayload()
    // Commission is computed on the fly, so a changed rule restates every past report for
    // this campaign. Only worth warning about when there is history to restate.
    const rulesChanged = JSON.stringify(payload.commission) !== this.state.originalCommission
    if (payload.id && rulesChanged && this.props.salescount > 0) {
      this.props.onRuleChangeConfirm(payload, this.props.salescount)
      return
    }
    this.setState({ saving: true })
    await this.props.onSave(payload)
    this.setState({ saving: false })
  }

  handleNext = () => {
    if (this.state.activeStep + 1 === STEPS.length) {
      this.handleSave()
      return
    }
    this.setState({ activeStep: this.state.activeStep + 1 })
  }

  handleBack = () => {
    this.setState({ activeStep: this.state.activeStep - 1 })
  }

  // The Next button only guards what the current step can actually get wrong, so a user is
  // not blocked by a field two steps away.
  stepBlocked = () => {
    const { activeStep, campaign } = this.state
    if (activeStep === 0) return !campaign.name || !campaign.affiliateid
    if (activeStep === 1) return campaign.codes.length === 0 || this.hasCodeErrors() || !!this.dateError()
    return !this.isValid()
  }

  /* Render */

  renderBasics = () => {
    const { campaign } = this.state
    const affiliates = this.props.affiliates || []

    return (
      <div>
        <Row className='mt-4'>
          <Col xs='12' md='4'>
            <TextField
              fullWidth
              required
              size='small'
              label='Kampanjan nimi'
              value={campaign.name}
              onChange={(e) => this.handleTextChange(e, 'name')}
              error={!campaign.name}
              helperText={!campaign.name ? 'Nimi on pakollinen' : ' '}
            />
          </Col>
          <Col xs='12' md='4'>
            <TextField
              fullWidth
              required
              select
              size='small'
              label='Kumppani'
              value={campaign.affiliateid}
              onChange={(e) => {
                const affiliate = affiliates.find((a) => a.id === e.target.value)
                this.setPath('affiliateid', e.target.value)
                this.setPath('affiliatename', affiliate ? affiliate.name || '' : '')
              }}
              error={!campaign.affiliateid}
              helperText={
                affiliates.length === 0
                  ? 'Lisää ensin kumppani Kumppanit-sivulla'
                  : !campaign.affiliateid
                    ? 'Kumppani on pakollinen'
                    : ' '
              }
            >
              {affiliates.map((affiliate) => (
                <MenuItem key={affiliate.id} value={affiliate.id}>
                  {affiliate.name}
                </MenuItem>
              ))}
            </TextField>
          </Col>
          <Col xs='12' md='4'>
            <TextField
              fullWidth
              select
              size='small'
              label='Tila'
              value={campaign.state}
              onChange={(e) => this.handleTextChange(e, 'state')}
              helperText='Tila ei estä koodin toimintaa verkkokaupassa — se kertoo, missä vaiheessa kampanja on'
            >
              {CAMPAIGN_STATES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Col>
        </Row>
        <Row className='mt-4'>
          <Col xs='12' md='8'>
            <TextField
              fullWidth
              multiline
              minRows={2}
              size='small'
              label='Muistiinpanot'
              value={campaign.notes}
              onChange={(e) => this.handleTextChange(e, 'notes')}
            />
          </Col>
        </Row>
      </div>
    )
  }

  renderCodesAndDates = () => {
    const { campaign } = this.state
    const dateError = this.dateError()

    return (
      <div>
        <Row className='mt-4'>
          <Col xs='12'>
            <Typography variant='subtitle2'>Koodit</Typography>
            <Typography variant='body2' color='text.secondary'>
              Koodi on se tunniste, joka tallentuu ostoon kun asiakas tulee kumppanin linkistä.
              Useampi koodi samassa kampanjassa antaa kanavakohtaisen vertailun raportilla.
            </Typography>
          </Col>
        </Row>
        {campaign.codes.map((entry, index) => {
          const error = this.codeError(index)
          return (
            <Row key={index} className='mt-3'>
              <Col xs='12' md='4'>
                <TextField
                  fullWidth
                  required
                  size='small'
                  label='Koodi'
                  value={entry.code}
                  onChange={(e) => this.handleCodeChange(index, 'code', e.target.value)}
                  error={!!error}
                  helperText={error || 'Tallennetaan pienillä kirjaimilla'}
                />
              </Col>
              <Col xs='12' md='4'>
                <TextField
                  fullWidth
                  size='small'
                  label='Kanava'
                  value={entry.label}
                  onChange={(e) => this.handleCodeChange(index, 'label', e.target.value)}
                  helperText='Esim. instagram, uutiskirje'
                />
              </Col>
              <Col xs='6' md='2' className='mt-1'>
                <FormControlLabel
                  control={
                    <Switch
                      size='small'
                      checked={(entry.state || 'active') === 'active'}
                      onChange={(e) => this.handleCodeChange(index, 'state', e.target.checked ? 'active' : 'paused')}
                    />
                  }
                  label={<span style={{ fontSize: 13 }}>Käytössä</span>}
                />
              </Col>
              <Col xs='6' md='2' className='mt-1'>
                <IconButton
                  size='small'
                  onClick={() => this.removeCode(index)}
                  disabled={campaign.codes.length === 1}
                >
                  <DeleteIcon fontSize='inherit' />
                </IconButton>
              </Col>
            </Row>
          )
        })}
        <Row className='mt-2'>
          <Col>
            <Button size='small' onClick={this.addCode}>
              Lisää koodi
            </Button>
          </Col>
        </Row>

        <Divider sx={{ mt: 4, mb: 1 }} />
        <Typography variant='subtitle2' sx={{ mt: 2 }}>
          Voimassaolo
        </Typography>

        <Row className='mt-3'>
          <Col xs='6' md='3'>
            <TextField
              fullWidth
              size='small'
              type='date'
              label='Alkaa'
              value={campaign.start || ''}
              onChange={(e) => this.handleTextChange(e, 'start')}
              InputLabelProps={{ shrink: true }}
            />
          </Col>
          <Col xs='6' md='3'>
            <TextField
              fullWidth
              size='small'
              type='date'
              label='Päättyy'
              value={campaign.end || ''}
              onChange={(e) => this.handleTextChange(e, 'end')}
              InputLabelProps={{ shrink: true }}
              error={!!dateError}
              helperText={dateError || ' '}
            />
          </Col>
          <Col xs='6' md='3'>
            <TextField
              fullWidth
              size='small'
              label='Kohdistusaika'
              value={campaign.attributionminutes}
              onChange={(e) => this.handleNumberChange(e, 'attributionminutes')}
              InputProps={{ endAdornment: <InputAdornment position='end'>min</InputAdornment> }}
              helperText='Kauanko linkin jälkeen ostot luetaan kumppanille'
            />
          </Col>
          <Col xs='6' md='3'>
            <TextField
              fullWidth
              size='small'
              label='Palkkiokatto'
              value={this.state.moneyInputs.budgetcap}
              onChange={(e) => this.handleMoneyChange(e, 'budgetcap')}
              InputProps={{ endAdornment: <InputAdornment position='end'>€</InputAdornment> }}
              helperText='0 = ei kattoa'
            />
          </Col>
        </Row>
        <Row className='mt-3'>
          <Col xs='12' md='6'>
            <TextField
              fullWidth
              size='small'
              label='Kampanjan kohdeosoite'
              value={campaign.landingurl}
              onChange={(e) => this.handleTextChange(e, 'landingurl')}
              helperText='Valinnainen — mihin kumppanin linkki ohjaa'
            />
          </Col>
        </Row>
      </div>
    )
  }

  renderRule = (type) => {
    const { campaign, moneyInputs } = this.state
    const rule = campaign.commission[type.key]
    const path = 'commission.' + type.key
    const isPercent = rule.type !== 'fixed'

    return (
      <Card key={type.key} sx={{ mt: 2, borderRadius: 2 }} variant='outlined'>
        <CardContent>
          <Row>
            <Col xs='12' md='4'>
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={!!rule.enabled}
                    onChange={(e) => this.handleSwitchChange(e, path + '.enabled')}
                  />
                }
                label={<b>{type.label}</b>}
              />
            </Col>
            {rule.enabled && (
              <Col xs='12' md='8'>
                <RadioGroup
                  row
                  value={isPercent ? 'percent' : 'fixed'}
                  onChange={(e) => this.setPath(path + '.type', e.target.value)}
                >
                  <FormControlLabel value='percent' control={<Radio size='small' />} label='Prosentti myynnistä' />
                  <FormControlLabel value='fixed' control={<Radio size='small' />} label='Kiinteä euromäärä' />
                </RadioGroup>
              </Col>
            )}
          </Row>

          {!rule.enabled && (
            <Typography variant='body2' color='text.secondary'>
              Ei palkkiota tästä myynnistä.
            </Typography>
          )}

          {rule.enabled && (
            <Row className='mt-2'>
              <Col xs='6' md='2'>
                <TextField
                  fullWidth
                  size='small'
                  label={isPercent ? 'Osuus' : 'Palkkio'}
                  value={moneyInputs[type.key + '.value']}
                  onChange={(e) => this.handleMoneyChange(e, type.key + '.value')}
                  InputProps={{
                    endAdornment: <InputAdornment position='end'>{isPercent ? '%' : '€'}</InputAdornment>,
                  }}
                />
              </Col>
              <Col xs='6' md='3'>
                {isPercent ? (
                  <TextField
                    fullWidth
                    select
                    size='small'
                    label='Laskentaperuste'
                    value={rule.basis || 'gross'}
                    onChange={(e) => this.setPath(path + '.basis', e.target.value)}
                  >
                    <MenuItem value='gross'>Myyntihinnasta (sis. alv)</MenuItem>
                    <MenuItem value='net'>Verottomasta (alv 0)</MenuItem>
                  </TextField>
                ) : (
                  <TextField
                    fullWidth
                    select
                    size='small'
                    label='Maksetaan'
                    value={rule.per || 'sale'}
                    onChange={(e) => this.setPath(path + '.per', e.target.value)}
                  >
                    <MenuItem value='sale'>Kertaalleen per osto</MenuItem>
                    <MenuItem value='unit'>
                      {type.key === 'eventtickets' ? 'Per lippu' : type.key === 'activities' ? 'Per osallistuja' : 'Per varaus'}
                    </MenuItem>
                  </TextField>
                )}
              </Col>
              <Col xs='6' md='2'>
                <TextField
                  fullWidth
                  size='small'
                  label='Vähimmäisosto'
                  value={moneyInputs[type.key + '.minorder']}
                  onChange={(e) => this.handleMoneyChange(e, type.key + '.minorder')}
                  InputProps={{ endAdornment: <InputAdornment position='end'>€</InputAdornment> }}
                  helperText='0 = ei rajaa'
                />
              </Col>
              <Col xs='6' md='2'>
                <TextField
                  fullWidth
                  size='small'
                  label='Katto / osto'
                  value={moneyInputs[type.key + '.maxpersale']}
                  onChange={(e) => this.handleMoneyChange(e, type.key + '.maxpersale')}
                  InputProps={{ endAdornment: <InputAdornment position='end'>€</InputAdornment> }}
                  helperText='0 = ei kattoa'
                />
              </Col>
            </Row>
          )}
        </CardContent>
      </Card>
    )
  }

  renderCommission = () => (
    <div>
      <Row className='mt-4'>
        <Col xs='12'>
          <Typography variant='subtitle2'>Palkkiot</Typography>
          <Typography variant='body2' color='text.secondary'>
            Jokainen myyntityyppi määritellään erikseen. Palkkiot lasketaan aina näillä
            säännöillä myös jo tehdyille myynneille, joten muutos näkyy heti raportin luvuissa.
          </Typography>
        </Col>
      </Row>
      {SALE_TYPES.map((type) => this.renderRule(type))}
    </div>
  )

  getStepContent = (step) => {
    if (step === 0) return this.renderBasics()
    if (step === 1) return this.renderCodesAndDates()
    return this.renderCommission()
  }

  renderCreate = () => {
    const { activeStep, saving } = this.state
    return (
      <div>
        <Stepper activeStep={activeStep}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {this.getStepContent(activeStep)}
        <Row className='mt-5'>
          <Col>
            <Button disabled={activeStep === 0 || saving} onClick={this.handleBack}>
              Takaisin
            </Button>{' '}
            <Button
              variant='contained'
              color='primary'
              onClick={this.handleNext}
              disabled={saving || this.stepBlocked()}
            >
              {activeStep === STEPS.length - 1 ? (saving ? 'Tallennetaan...' : 'Tallenna kampanja') : 'Seuraava'}
            </Button>
          </Col>
        </Row>
      </div>
    )
  }

  renderEdit = () => {
    const { saving } = this.state
    return (
      <div>
        {this.renderBasics()}
        <Divider sx={{ mt: 4 }} />
        {this.renderCodesAndDates()}
        <Divider sx={{ mt: 4 }} />
        {this.renderCommission()}
        <Row className='mt-5'>
          <Col>
            <Button onClick={this.props.onCancel} disabled={saving}>
              Peruuta
            </Button>{' '}
            <Button
              variant='contained'
              color='primary'
              onClick={this.handleSave}
              disabled={saving || !this.isValid()}
            >
              {saving ? 'Tallennetaan...' : 'Tallenna'}
            </Button>
          </Col>
        </Row>
      </div>
    )
  }

  render() {
    const { campaign } = this.state
    return (
      <div style={{ padding: 20, backgroundColor: 'white' }}>
        <Row>
          <Col>
            <Typography variant='h6'>{campaign.id ? 'Muokkaa kampanjaa' : 'Lisää kampanja'}</Typography>
          </Col>
          <Col xs='auto'>
            <Button onClick={this.props.onCancel} disabled={this.state.saving}>
              Palaa listaan
            </Button>
          </Col>
        </Row>
        {campaign.id ? this.renderEdit() : this.renderCreate()}
      </div>
    )
  }
}

export default AffiliateCampaignEditor
